// components/StoryPicker.tsx — post-auth picker for the user's saved stories.
//
// Two roles:
//   1. Boot-time gate after login + character setup, before the game UI,
//      whenever the user has 0 or >1 active stories. (For exactly 1 story
//      App.tsx skips this picker — see STORIES_CONTRACT §8.)
//   2. Mid-game "switch story" surface, opened from the user-chip menu.
//      In that mode the parent passes `onClose` so we render an X.
//
// Everything that requires a network call (load list, create, delete,
// activate) is owned by the parent — this component is a presentation-only
// view. It receives `stories` + `activeStoryId` already loaded and emits
// `onChoose`, `onStartNew`, `onDelete`, `onClose`.
//
// Visual language: the same dark gradient as the rest of the post-auth
// game UI, with an inset card. NOT the modalBackdrop (this is a full-screen
// gate when used at boot) and NOT the Login surface's CSS-var theming
// (those vars are scoped to Login.tsx). All styles are inline here per
// the additive-only constraint.

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useTranslation } from "react-i18next";
import {
  SCENARIO_PRESENTATIONS,
  type ScenarioPresentation,
} from "../scenarios/presentation";
import type { StoryListEntry } from "../services/storiesApi";
import StoryIdeaForm from "./StoryIdeaForm";

export interface StoryPickerProps {
  /** Loaded server response — already fetched by the parent; this component never fetches. */
  stories: StoryListEntry[];
  /** Currently active story id, for the "active" highlight. */
  activeStoryId: string | null;
  /** User picked an existing story to continue. */
  onChoose: (id: string) => void;
  /** User clicked "Start a new story" → parent opens the ScenarioPicker. */
  onStartNew: () => void;
  /** User confirmed deletion of a story. */
  onDelete: (id: string) => void;
  /**
   * User saved an inline rename via the pencil affordance. The parent
   * performs the network call (PATCH /api/stories/:id) and updates its
   * local state. The component surfaces failures inline; the parent
   * should let exceptions bubble up via the returned promise rejecting
   * so this component can revert. Active stories are renamable;
   * finished ones show the pencil disabled.
   */
  onRename: (id: string, name: string) => Promise<void> | void;
  /**
   * Optional close — only present when used as "switch story" mid-game,
   * not the boot flow. When undefined no close button is shown and ESC
   * does nothing.
   */
  onClose?: () => void;
}

// Loose shape of the presentation entry that we actually consume here.
// `genre` is read defensively — a parallel agent is adding the field, and
// we want this code to compile both before and after that change.
type LoosePresentation = ScenarioPresentation & { genre?: string };

interface ResolvedPresentation {
  pillLabel: string;
  genre: string;
}

function resolvePresentation(scenarioId: string): ResolvedPresentation {
  const entry = SCENARIO_PRESENTATIONS[scenarioId] as
    | LoosePresentation
    | undefined;
  if (!entry) {
    return { pillLabel: "Unknown scenario", genre: "" };
  }
  return {
    pillLabel: entry.pillLabel,
    // Optional access — the field may not exist yet in a parallel branch.
    genre: typeof entry.genre === "string" ? entry.genre : "",
  };
}

/**
 * Format an ISO timestamp as a short relative time using the game-namespace
 * translations. Falls back to a dash for invalid input. Plural variants
 * resolve via i18next's `_one` / `_other` suffix machinery.
 */
function formatRelative(
  iso: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
  now: Date = new Date(),
): string {
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return "—";
  const diffSec = Math.max(0, Math.floor((now.getTime() - ts) / 1000));
  if (diffSec < 45) return t("storyPicker.relative.justNow");
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return t("storyPicker.relative.minute", { count: diffMin });
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return t("storyPicker.relative.hour", { count: diffHr });
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return t("storyPicker.relative.day", { count: diffDay });
  const diffWk = Math.floor(diffDay / 7);
  if (diffWk < 5) return t("storyPicker.relative.week", { count: diffWk });
  const diffMo = Math.floor(diffDay / 30);
  if (diffMo < 12) return t("storyPicker.relative.month", { count: diffMo });
  const diffYr = Math.floor(diffDay / 365);
  return t("storyPicker.relative.year", { count: diffYr });
}

export default function StoryPicker({
  stories,
  activeStoryId,
  onChoose,
  onStartNew,
  onDelete,
  onRename,
  onClose,
}: StoryPickerProps) {
  const { t } = useTranslation(["game", "common", "scenarios", "bookshelf"]);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(
    null,
  );
  // Inline-edit state: which story id is currently in edit mode (null = none),
  // the working value of its input, and a per-row error string used to
  // surface a failed PATCH without yanking the user out of the picker.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const [rowError, setRowError] = useState<string | null>(null);
  // Guard against double-fire: Enter + onBlur can race when the user presses
  // Enter (which also moves focus); the ref tracks "we've already started a
  // save for this edit session" so we don't fire two PATCHes back-to-back.
  const savingRef = useRef(false);
  const firstContinueRef = useRef<HTMLButtonElement | null>(null);
  const startNewRef = useRef<HTMLButtonElement | null>(null);

  // Sort: active first, then most-recently-played. Defensive — the server
  // already orders by lastPlayedAt desc, but we don't want a re-render
  // ordering glitch if a parent passes the list in a different order.
  const sortedStories = useMemo(() => {
    const copy = [...stories];
    copy.sort((a, b) => {
      const aActive = a.id === activeStoryId ? 1 : 0;
      const bActive = b.id === activeStoryId ? 1 : 0;
      if (aActive !== bActive) return bActive - aActive;
      return Date.parse(b.lastPlayedAt) - Date.parse(a.lastPlayedAt);
    });
    return copy;
  }, [stories, activeStoryId]);

  // ── Inline rename helpers ───────────────────────────────────────────────
  // Enter pencil-edit mode for a story row. Resets any error from a prior
  // edit attempt and seeds the input with the current name.
  function startEditing(story: StoryListEntry) {
    if (story.status === "finished") return; // finalised rows are frozen
    savingRef.current = false;
    setRowError(null);
    setEditingId(story.id);
    setEditingValue(story.name || "");
  }

  // Cancel without saving — bail out and clear any error.
  function cancelEditing() {
    savingRef.current = false;
    setEditingId(null);
    setEditingValue("");
    setRowError(null);
  }

  // Commit the edit. Empty / whitespace-only / unchanged values just exit
  // edit mode silently. On PATCH failure the row stays in edit mode and
  // shows an inline error so the user can fix it or cancel.
  async function commitEditing(story: StoryListEntry) {
    if (savingRef.current) return;
    const trimmed = editingValue.trim();
    if (trimmed.length === 0 || trimmed === story.name) {
      cancelEditing();
      return;
    }
    savingRef.current = true;
    try {
      await onRename(story.id, trimmed);
      // Parent has updated its `stories` prop; we'll see the new name on
      // re-render. Drop out of edit mode regardless of any stale local
      // value the input may still hold.
      setEditingId(null);
      setEditingValue("");
      setRowError(null);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : t("game:storyPicker.renameFailed");
      setRowError(msg);
      // Keep the user in edit mode so they can retry or cancel.
      savingRef.current = false;
    }
  }

  // Focus management on mount: prefer the first Continue button (so Enter
  // resumes the most relevant story), falling back to "Start a new story"
  // when the list is empty.
  useEffect(() => {
    if (firstContinueRef.current) {
      firstContinueRef.current.focus();
    } else if (startNewRef.current) {
      startNewRef.current.focus();
    }
  }, []);

  // ESC closes when used as a switch-story modal-ish surface.
  useEffect(() => {
    if (!onClose) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const isEmpty = sortedStories.length === 0;

  return (
    <div style={shellStyle}>
      <div style={grainStyle} />
      <div style={cardStyle}>
        {onClose && (
          <button
            type="button"
            aria-label={t("common:buttons.close")}
            style={closeBtnStyle}
            onClick={onClose}
          >
            ×
          </button>
        )}

        <h1 style={titleStyle}>{t("game:storyPicker.title")}</h1>
        <div style={subtitleStyle}>{t("game:storyPicker.subtitle")}</div>

        <button
          ref={startNewRef}
          type="button"
          style={startNewStyle}
          onClick={onStartNew}
        >
          {t("game:storyPicker.startNew")}
        </button>

        {isEmpty ? (
          <div style={emptyStyle}>{t("game:storyPicker.empty")}</div>
        ) : (
          <ul role="list" style={listStyle}>
            {sortedStories.map((story, idx) => {
              const presentation = resolvePresentation(story.scenarioId);
              const isActive = story.id === activeStoryId;
              const isConfirming = confirmingDeleteId === story.id;
              const displayName =
                story.name ||
                t("bookshelf:bookReader.untitled", { defaultValue: "Untitled" });
              const translatedPillLabel = t(`scenarios:${story.scenarioId}.pillLabel`, {
                defaultValue: presentation.pillLabel,
              });
              const translatedGenre = t(`scenarios:${story.scenarioId}.genre`, {
                defaultValue: presentation.genre,
              });
              return (
                <li
                  key={story.id}
                  role="listitem"
                  style={{
                    ...rowStyle,
                    ...(isActive ? rowActiveStyle : null),
                  }}
                >
                  <div style={rowLeftStyle}>
                    <div style={rowMetaRowStyle}>
                      <span style={pillStyle}>{translatedPillLabel}</span>
                      {translatedGenre && (
                        <span style={genreStyle}>{translatedGenre}</span>
                      )}
                      {isActive && (
                        <span style={activeTagStyle}>
                          {t("game:storyPicker.activeTag")}
                        </span>
                      )}
                      {story.status === "finished" && (
                        <span style={finishedTagStyle}>
                          {t("game:storyPicker.finishedTag")}
                        </span>
                      )}
                      {story.requiresSubscriptionToWrite && (
                        // Forever-default lock (STORIES_CONTRACT §1).
                        // Clicking the row STILL fires onChoose — opening a
                        // locked story for browsing is allowed; the in-game
                        // paywall overlay enforces the write block.
                        <span style={lockedTagStyle} title={t("game:storyPicker.lockedTitle")}>
                          {t("game:storyPicker.lockedTag")}
                        </span>
                      )}
                    </div>
                    {editingId === story.id ? (
                      <div style={storyNameEditRowStyle}>
                        <input
                          autoFocus
                          type="text"
                          autoCapitalize="words"
                          autoCorrect="off"
                          value={editingValue}
                          onChange={(e) =>
                            setEditingValue(e.target.value.slice(0, 120))
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              void commitEditing(story);
                            } else if (e.key === "Escape") {
                              e.preventDefault();
                              e.stopPropagation();
                              cancelEditing();
                            }
                          }}
                          onBlur={() => {
                            // onBlur fires after Enter has already moved focus
                            // to the next element. The savingRef guard inside
                            // commitEditing prevents a double-save.
                            void commitEditing(story);
                          }}
                          maxLength={120}
                          style={storyNameInputStyle}
                          aria-label={t("game:storyPicker.renameAriaInput")}
                        />
                        {rowError && (
                          <div style={rowErrorStyle}>{rowError}</div>
                        )}
                      </div>
                    ) : (
                      <div style={storyNameRowStyle}>
                        <span style={storyNameStyle}>{displayName}</span>
                        {story.status === "finished" ? (
                          <span
                            style={pencilDisabledStyle}
                            aria-hidden="true"
                            title={t("game:storyPicker.renameFinishedTitle")}
                          >
                            ✎
                          </span>
                        ) : (
                          <button
                            type="button"
                            aria-label={t("game:storyPicker.renameAria", { name: displayName })}
                            title={t("game:storyPicker.renameTitle")}
                            style={pencilBtnStyle}
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditing(story);
                            }}
                          >
                            ✎
                          </button>
                        )}
                      </div>
                    )}
                    <div style={lastPlayedStyle}>
                      {t("game:storyPicker.lastPlayed", {
                        relative: formatRelative(story.lastPlayedAt, t),
                      })}
                    </div>
                  </div>

                  <div style={rowRightStyle}>
                    {isConfirming ? (
                      <div style={confirmRowStyle}>
                        <span style={confirmTextStyle}>
                          {t("game:storyPicker.deletePrompt")}
                        </span>
                        <button
                          type="button"
                          style={confirmYesStyle}
                          onClick={() => {
                            onDelete(story.id);
                            setConfirmingDeleteId(null);
                          }}
                        >
                          {t("game:storyPicker.yes")}
                        </button>
                        <button
                          type="button"
                          style={confirmNoStyle}
                          onClick={() => setConfirmingDeleteId(null)}
                        >
                          {t("common:buttons.cancel")}
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          ref={idx === 0 ? firstContinueRef : undefined}
                          type="button"
                          style={continueBtnStyle}
                          onClick={() => onChoose(story.id)}
                        >
                          {t("game:storyPicker.continueCta")}
                        </button>
                        <button
                          type="button"
                          aria-label={t("game:storyPicker.deleteAria", { name: displayName })}
                          style={deleteBtnStyle}
                          onClick={() => setConfirmingDeleteId(story.id)}
                        >
                          {t("game:storyPicker.deleteCta")}
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/*
          Story-ideas disclosure — visible to all signed-in users (free,
          subscriber, locked-stories). Renders below the existing rows and
          the "Start a new story" CTA. Survives both the boot-gate and the
          mid-game switcher variants of this picker. See STORIES_CONTRACT
          §11 and ADMIN_SOCKET_CONTRACT for the wire.
        */}
        <StoryIdeaForm />
      </div>
    </div>
  );
}

// ── Styles (inline to keep this file additive — see header) ─────────────

const shellStyle: CSSProperties = {
  position: "relative",
  minHeight: "100vh",
  background:
    "radial-gradient(ellipse at top, #1a1410 0%, #0d0907 60%, #050302 100%)",
  color: "#e8d9b8",
  fontFamily: "'Cormorant Garamond', 'EB Garamond', Georgia, serif",
  display: "flex",
  // flex-start (not center) so when the card is taller than the viewport
  // its top edge stays visible and the page scrolls naturally to reveal
  // the rest. With `center`, an oversize card has its top pushed above
  // the scroll origin and cannot be reached.
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "48px 24px",
};

const grainStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  pointerEvents: "none",
  backgroundImage:
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.6 0 0 0 0 0.5 0 0 0 0 0.3 0 0 0 0.08 0'/></filter><rect width='200' height='200' filter='url(%23n)'/></svg>\")",
  opacity: 0.5,
  zIndex: 1,
  mixBlendMode: "overlay",
};

const cardStyle: CSSProperties = {
  position: "relative",
  zIndex: 2,
  width: "100%",
  maxWidth: 640,
  padding: "44px 40px",
  border: "1px solid rgba(212,165,116,0.25)",
  background: "rgba(20,14,10,0.7)",
  boxShadow: "0 0 60px rgba(0,0,0,0.6)",
  borderRadius: 2,
};

const closeBtnStyle: CSSProperties = {
  position: "absolute",
  top: 12,
  right: 14,
  background: "transparent",
  border: "none",
  color: "#7a6243",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "1.2rem",
  cursor: "pointer",
  padding: 6,
  lineHeight: 1,
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontFamily: "'IM Fell English', 'Cormorant Garamond', Georgia, serif",
  fontStyle: "italic",
  fontSize: "2.2rem",
  fontWeight: 400,
  color: "#e8c896",
  letterSpacing: "0.02em",
  textAlign: "center",
  textShadow: "0 0 24px rgba(212,165,116,0.25)",
};

const subtitleStyle: CSSProperties = {
  marginTop: 4,
  marginBottom: 24,
  fontStyle: "italic",
  color: "#8a7355",
  fontSize: "0.9rem",
  letterSpacing: "0.06em",
  textAlign: "center",
};

const startNewStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  background: "rgba(212,165,116,0.14)",
  border: "1px solid rgba(212,165,116,0.5)",
  color: "#e8c896",
  fontFamily: "'IM Fell English', Georgia, serif",
  fontSize: "1.05rem",
  letterSpacing: "0.04em",
  padding: "12px 16px",
  cursor: "pointer",
  borderRadius: 2,
  marginBottom: 22,
};

const emptyStyle: CSSProperties = {
  padding: "16px 0",
  color: "#7a6243",
  fontStyle: "italic",
  fontSize: "0.9rem",
  textAlign: "center",
};

const listStyle: CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  padding: "14px 16px",
  border: "1px solid rgba(212,165,116,0.18)",
  background: "rgba(28,20,14,0.55)",
  borderRadius: 2,
};

const rowActiveStyle: CSSProperties = {
  borderLeft: "3px solid #d4a574",
  paddingLeft: 14,
};

const rowLeftStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const rowMetaRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
};

const pillStyle: CSSProperties = {
  display: "inline-block",
  padding: "2px 8px",
  background: "rgba(212,165,116,0.08)",
  border: "1px solid rgba(212,165,116,0.28)",
  borderRadius: 2,
  color: "#d4b88a",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.7rem",
  letterSpacing: "0.06em",
};

const genreStyle: CSSProperties = {
  color: "#7a6243",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.7rem",
  fontStyle: "italic",
};

const activeTagStyle: CSSProperties = {
  display: "inline-block",
  padding: "2px 6px",
  border: "1px solid rgba(212,165,116,0.45)",
  background: "rgba(212,165,116,0.16)",
  color: "#e8c896",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.66rem",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  borderRadius: 2,
};

const finishedTagStyle: CSSProperties = {
  display: "inline-block",
  padding: "2px 6px",
  border: "1px dashed rgba(154,214,177,0.4)",
  color: "#9ad6b1",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.66rem",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  borderRadius: 2,
};

// Forever-default lock badge — shown on rows whose scenario doesn't match
// the user's forever_default_scenario_id (free tier only). The story is
// still openable for browsing; the in-game paywall handles writes.
const lockedTagStyle: CSSProperties = {
  display: "inline-block",
  padding: "2px 6px",
  border: "1px dashed rgba(212,165,116,0.45)",
  background: "rgba(212,165,116,0.07)",
  color: "#c8a878",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.66rem",
  letterSpacing: "0.06em",
  textTransform: "lowercase",
  borderRadius: 2,
};

const storyNameStyle: CSSProperties = {
  color: "#e8d9b8",
  fontStyle: "italic",
  fontSize: "1.1rem",
  lineHeight: 1.3,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// Wraps the story name + pencil so the icon sits flush right of the title.
// Width stays 100% of rowLeftStyle so the pencil's click target lines up
// with the name itself rather than floating off in the row's gutter.
const storyNameRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 8,
  minWidth: 0,
};

const pencilBtnStyle: CSSProperties = {
  flex: "0 0 auto",
  background: "transparent",
  border: "none",
  color: "#7a6243",
  fontSize: "0.95rem",
  lineHeight: 1,
  cursor: "pointer",
  padding: "2px 4px",
  borderRadius: 2,
  // Inherit fontFamily from the row so the glyph picks up Cormorant if the
  // system has it; the unicode pencil renders in any font.
};

const pencilDisabledStyle: CSSProperties = {
  flex: "0 0 auto",
  color: "#3a2f23",
  fontSize: "0.95rem",
  lineHeight: 1,
  padding: "2px 4px",
  cursor: "not-allowed",
  opacity: 0.5,
};

// Edit-mode wraps the input + an optional error line below it.
const storyNameEditRowStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  minWidth: 0,
};

const storyNameInputStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  background: "rgba(28,20,14,0.85)",
  border: "1px solid rgba(212,165,116,0.45)",
  color: "#e8d9b8",
  fontFamily: "'Cormorant Garamond', 'EB Garamond', Georgia, serif",
  fontStyle: "italic",
  fontSize: "1.1rem",
  lineHeight: 1.3,
  padding: "4px 8px",
  outline: "none",
  borderRadius: 2,
};

const rowErrorStyle: CSSProperties = {
  color: "#c47a5a",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.7rem",
  letterSpacing: "0.04em",
};

const lastPlayedStyle: CSSProperties = {
  color: "#7a6243",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.7rem",
  letterSpacing: "0.04em",
};

const rowRightStyle: CSSProperties = {
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const continueBtnStyle: CSSProperties = {
  background: "transparent",
  border: "1px solid rgba(212,165,116,0.45)",
  color: "#e8c896",
  fontFamily: "'IM Fell English', Georgia, serif",
  fontSize: "0.95rem",
  letterSpacing: "0.04em",
  padding: "8px 16px",
  cursor: "pointer",
  borderRadius: 2,
  transition: "all 0.18s ease-out",
};

const deleteBtnStyle: CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#7a6243",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.7rem",
  cursor: "pointer",
  textDecoration: "underline",
  padding: 4,
  letterSpacing: "0.06em",
};

const confirmRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const confirmTextStyle: CSSProperties = {
  color: "#c47a5a",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.74rem",
  letterSpacing: "0.04em",
};

const confirmYesStyle: CSSProperties = {
  background: "rgba(196,122,90,0.18)",
  border: "1px solid rgba(196,122,90,0.55)",
  color: "#e8b09a",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.72rem",
  letterSpacing: "0.06em",
  padding: "5px 10px",
  cursor: "pointer",
  borderRadius: 2,
};

const confirmNoStyle: CSSProperties = {
  background: "transparent",
  border: "1px solid rgba(212,165,116,0.28)",
  color: "#a89070",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.72rem",
  letterSpacing: "0.06em",
  padding: "5px 10px",
  cursor: "pointer",
  borderRadius: 2,
};
